import { useCallback, useEffect, useState } from 'react';
import { adminGetSettings, adminUpdateSetting } from '../services/api';

export type MasterKey =
  | 'master_categories'
  | 'master_sizes'
  | 'master_colors'
  | 'master_thread_colors'
  | 'master_fonts';

export type MasterOptions = Record<MasterKey, string[]>;

const EMPTY: MasterOptions = {
  master_categories: [],
  master_sizes: [],
  master_colors: [],
  master_thread_colors: [],
  master_fonts: [],
};

const normalizeList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(v => String(v)).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    // tolerate legacy comma-separated values
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  return [];
};

export const useMasterOptions = () => {
  const [options, setOptions] = useState<MasterOptions>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(() => {
    setLoading(true);
    adminGetSettings()
      .then((settings: Record<string, any>) => {
        setOptions({
          master_categories: normalizeList(settings?.master_categories),
          master_sizes: normalizeList(settings?.master_sizes),
          master_colors: normalizeList(settings?.master_colors),
          master_thread_colors: normalizeList(settings?.master_thread_colors),
          master_fonts: normalizeList(settings?.master_fonts),
        });
      })
      .catch(e => setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load options'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const addOption = useCallback(
    async (key: MasterKey, value: string): Promise<string> => {
      const cleaned = value.trim();
      if (!cleaned) return cleaned;
      const current = options[key];
      // Case-insensitive match: if "Red" already exists and admin types "red",
      // reuse the existing entry's casing instead of creating a duplicate.
      const existing = current.find(c => c.toLowerCase() === cleaned.toLowerCase());
      if (existing) return existing;

      const next = [...current, cleaned].sort((a, b) => a.localeCompare(b));
      setOptions(prev => ({ ...prev, [key]: next }));
      try {
        await adminUpdateSetting(key, next);
      } catch (e) {
        // best-effort -- the in-memory list still has the new value so the
        // editor flows continue, but log so we know server is stale.
        // eslint-disable-next-line no-console
        console.warn('Failed to persist master option', key, e);
      }
      return cleaned;
    },
    [options]
  );

  return { options, loading, error, addOption, reload: load };
};
